// ─────────────────────────────────────────────
// src/pages/AfterSchoolProgramsPage.jsx
// SEO landing page for before- and after-school program search intent
// ("STEM after-school programs in Fairfax VA").
// ─────────────────────────────────────────────
import { useEffect } from "react";
import { StarField, GlassCard, Btn, SectionLabel, SectionTitle } from "../components/UI";
import Footer from "../components/Footer";

export default function AfterSchoolProgramsPage({ setPage }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__after-school-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "STEM Before & After School Programs in Fairfax, VA",
      "url": "https://www.marsacademy.ai/after-school-programs",
      "description": "Mars Academy's STEM program runs before and after school, bringing hands-on coding, AI, and technology classes to elementary students at schools across Fairfax County, Virginia.",
      "about": {
        "@type": "EducationalOrganization",
        "@id": "https://www.marsacademy.ai/#organization"
      }
    });
    document.head.appendChild(script);
    return () => document.getElementById('__after-school-schema')?.remove();
  }, []);

  const steps = [
    { icon: "🏫", title: "Offered On-Site at Your School",  desc: "Mars Academy partners directly with schools, so classes run right at your child's school in a space your family already knows, with no extra driving or pickup to coordinate." },
    { icon: "📅", title: "One Class a Week",                 desc: "A typical session runs about 6 classes, one per week, roughly 2 hours each, scheduled before or after the school day depending on your school. Exact days and times depend on your school's schedule." },
    { icon: "💻", title: "Everything Provided",              desc: "Laptops are provided for every class. Students just need a free Scratch account (scratch.mit.edu) before their first session, and we send setup instructions after enrollment." },
    { icon: "🧑‍🏫", title: "Taught by Vetted Instructors",     desc: "Every instructor is FCPS background checked and certified on the Mars Academy curriculum before ever leading a class." },
  ];

  return (
    <div>
      {/* Header */}
      <section style={{ padding:"8rem 2rem 4rem", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <StarField count={40} />
        <div style={{ position:"relative", zIndex:2, maxWidth:760, margin:"0 auto" }}>
          <SectionLabel color="#22C55E">Before & After School Programs</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,5vw,3.4rem)", lineHeight:1.2, marginBottom:"1rem" }}>
            STEM Before & After School Programs in Fairfax, VA
          </h1>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"1rem", fontWeight:600, lineHeight:1.8, maxWidth:640, margin:"0 auto" }}>
            Mars Academy brings a hands-on STEM and coding program, offered before and after school, directly into elementary and middle schools across Fairfax County, Virginia, so kids can learn real technology skills without leaving their school building.
          </p>
        </div>
      </section>

      {/* What it is */}
      <section style={{ padding:"1rem 2rem 3rem", maxWidth:900, margin:"0 auto" }}>
        <GlassCard style={{ padding:"2rem 2.2rem" }}>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", marginBottom:"0.8rem", color:"#FFD93D" }}>What Is Mars Academy's Before &amp; After School Program?</h2>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.95rem", lineHeight:1.85, fontWeight:600 }}>
            It's a single, comprehensive program, offered before and after school depending on the school, that introduces elementary and middle school students to the technology shaping their future: coding, artificial intelligence, internet safety, cloud computing, and digital infrastructure, all through hands-on lessons. No prior coding experience is needed; the program meets every child where they are, from a first-time keyboard user to a kid who's already curious about programming.
          </p>
        </GlassCard>
      </section>

      {/* How it works */}
      <section style={{ padding:"1rem 2rem 3rem", maxWidth:1100, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:"2.2rem" }}>
          <SectionLabel color="#06B6D4">How It Works</SectionLabel>
          <SectionTitle center>What to Expect</SectionTitle>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:"1.2rem" }}>
          {steps.map((s, i) => (
            <GlassCard key={i} style={{ padding:"1.5rem" }}>
              <div style={{ fontSize:"1.8rem", marginBottom:"0.6rem" }}>{s.icon}</div>
              <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", marginBottom:"0.5rem" }}>{s.title}</h3>
              <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.85rem", lineHeight:1.65, fontWeight:600 }}>{s.desc}</p>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Registration */}
      <section style={{ padding:"1rem 2rem 5rem", maxWidth:780, margin:"0 auto" }}>
        <div style={{ background:"linear-gradient(135deg,rgba(255,107,107,0.12),rgba(155,92,246,0.12))", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:28, padding:"2.6rem 2rem", textAlign:"center" }}>
          <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.8rem" }}>How Schools &amp; Parents Get Started</h2>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.94rem", fontWeight:600, lineHeight:1.8, maxWidth:600, margin:"0 auto 1.6rem" }}>
            Registration depends on your school. At some schools it's coordinated through the PTA, while at others parents sign up directly on our website. Is your child's school in Fairfax County not on the program yet? Reach out and we'll let you know if we're headed there next.
          </p>
          <div style={{ display:"flex", gap:"0.9rem", justifyContent:"center", flexWrap:"wrap" }}>
            <Btn onClick={() => setPage("login")} variant="primary">🚀 Sign Up Now</Btn>
            <Btn onClick={() => setPage("faq")} variant="secondary">Read the FAQ</Btn>
          </div>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
